import { createRouter, createWebHashHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import type { App } from 'vue'
import { Layout, getParentLayout } from '@/utils/routerHelper'
import { useI18n } from '@/hooks/web/useI18n'
import { NO_RESET_WHITE_LIST } from '@/constants'

const { t } = useI18n()

export const constantRouterMap: AppRouteRecordRaw[] = [
  {
    path: '/',
    component: Layout,
    redirect: '/users/list',
    name: 'Root',
    meta: {
      hidden: true
    }
  },
  {
    path: '/redirect',
    component: Layout,
    name: 'RedirectWrap',
    children: [
      {
        path: '/redirect/:path(.*)',
        name: 'Redirect',
        component: () => import('@/views/Redirect/Redirect.vue'),
        meta: {}
      }
    ],
    meta: {
      hidden: true,
      noTagsView: true
    }
  },
  {
    path: '/login',
    component: () => import('@/views/Login/Login.vue'),
    name: 'Login',
    meta: {
      hidden: true,
      title: t('router.login'),
      noTagsView: true
    }
  },
  {
    path: '/personal',
    component: Layout,
    redirect: '/personal/personal-center',
    name: 'Personal',
    meta: {
      title: t('router.personal'),
      hidden: true,
      canTo: true
    },
    children: [
      {
        path: 'personal-center',
        component: () => import('@/views/Personal/PersonalCenter/PersonalCenter.vue'),
        name: 'PersonalCenter',
        meta: {
          title: t('router.personalCenter'),
          hidden: true,
          canTo: true
        }
      }
    ]
  },
  {
    path: '/404',
    component: () => import('@/views/Error/404.vue'),
    name: 'NoFind',
    meta: {
      hidden: true,
      title: '404',
      noTagsView: true
    }
  }
]

export const asyncRouterMap: AppRouteRecordRaw[] = [
  {
    path: '/dashboard',
    component: Layout,
    redirect: '/ai-traders/trader-arena', // Default to Trader Arena
    name: 'Dashboard',
    meta: {
      title: t('router.dashboard'),
      icon: 'vi-ant-design:dashboard-filled',
      alwaysShow: true,
      hidden: true // Hidden: Template page (US1)
    },
    children: [
      {
        path: 'analysis',
        component: () => import('@/views/Dashboard/Analysis.vue'),
        name: 'Analysis',
        meta: {
          title: t('router.analysis'),
          noCache: true,
          affix: true
        }
      },
      {
        path: 'workplace',
        component: () => import('@/views/Dashboard/Workplace.vue'),
        name: 'Workplace',
        meta: {
          title: t('router.workplace'),
          noCache: true
        }
      }
    ]
  },
  // ========================================
  // Member System (会员系统)
  // ========================================
  {
    path: '/users',
    component: Layout,
    redirect: '/users/list',
    name: 'MemberSection',
    meta: {
      title: '会员系统',
      icon: 'vi-mdi:crown',
      alwaysShow: true,
      order: 0
    },
    children: [
      {
        path: 'menu-access',
        component: () => import('@/views/MemberSystem/AdminMenuAccess.vue'),
        name: 'AdminMenuAccess',
        meta: {
          title: '菜单权限',
          icon: 'vi-mdi:shield-account-outline',
          noCache: true,
          order: 0.75
        }
      },
      {
        path: 'list',
        component: () => import('@/views/Users/UserManagement.vue'),
        name: 'UserManagement',
        meta: {
          title: '用户管理',
          icon: 'vi-mdi:account-group',
          noCache: true,
          order: 1
        }
      },
      {
        path: 'trial-management',
        component: () => import('@/views/MemberSystem/TrialManagement.vue'),
        name: 'TrialManagement',
        meta: {
          title: '试用管理',
          icon: 'vi-mdi:gift-outline',
          noCache: true,
          order: 1.5
        }
      },
      {
        path: 'settings',
        component: () => import('@/views/MemberSystem/MemberSystemSettings.vue'),
        name: 'MemberSystemSettings',
        meta: {
          title: '系统设置',
          icon: 'vi-mdi:cog',
          noCache: true,
          order: 2
        }
      }
    ]
  },
  // ========================================
  // Referral 邀请管理 (#325 PR 3)
  // ========================================
  {
    path: '/referral',
    component: Layout,
    redirect: '/referral/configs',
    name: 'ReferralSection',
    meta: {
      title: '邀请管理',
      icon: 'vi-mdi:account-multiple-plus-outline',
      alwaysShow: true,
      order: 0.25
    },
    children: [
      {
        path: 'configs',
        component: () => import('@/views/Referral/RewardConfigs.vue'),
        name: 'ReferralRewardConfigs',
        meta: {
          title: '奖励配置',
          icon: 'vi-mdi:tune-variant',
          noCache: true,
          order: 1
        }
      },
      {
        path: 'users',
        component: () => import('@/views/Referral/UserGroups.vue'),
        name: 'ReferralUserGroups',
        meta: {
          title: '用户分组',
          icon: 'vi-mdi:account-group-outline',
          noCache: true,
          order: 2
        }
      },
      {
        path: 'reward-events',
        component: () => import('@/views/Referral/RewardEvents.vue'),
        name: 'ReferralRewardEvents',
        meta: {
          title: '奖励事件',
          icon: 'vi-mdi:history',
          noCache: true,
          order: 3
        }
      },
      {
        path: 'payouts',
        component: () => import('@/views/Referral/PayoutQueue.vue'),
        name: 'ReferralPayoutQueue',
        meta: {
          title: '打款队列',
          icon: 'vi-mdi:cash-multiple',
          noCache: true,
          order: 4
        }
      }
    ]
  },
  // ========================================
  // Push System (消息源管理 / 推送渠道 / Worker 运行状态)
  // ========================================
  {
    path: '/push-system',
    component: Layout,
    redirect: '/push-system/message-sources',
    name: 'PushSystemSection',
    meta: {
      title: '推送系统',
      icon: 'vi-mdi:broadcast',
      alwaysShow: true,
      order: 0.5
    },
    children: [
      {
        path: 'message-sources',
        component: () => import('@/views/MessageSources/MessageSources.vue'),
        name: 'MessageSourcesManagement',
        meta: {
          title: '消息源管理',
          icon: 'vi-mdi:database-sync-outline',
          noCache: true,
          order: 1
        }
      },
      {
        path: 'push-channels',
        component: () => import('@/views/PushChannels/PushChannels.vue'),
        name: 'PushChannelsManagement',
        meta: {
          title: '推送渠道',
          icon: 'vi-mdi:send-outline',
          noCache: true,
          order: 2
        }
      },
      {
        path: 'subscription-channel-permissions',
        component: () =>
          import('@/views/SubscriptionChannelPermissions/SubscriptionChannelPermissions.vue'),
        name: 'SubscriptionChannelPermissions',
        meta: {
          title: '频道权限管理',
          icon: 'vi-mdi:key-variant',
          noCache: true,
          order: 2.25
        }
      },
      {
        path: 'messages',
        component: () => import('@/views/PushMessages/PushMessages.vue'),
        name: 'PushMessages',
        meta: {
          title: '推送消息',
          icon: 'vi-mdi:message-text-outline',
          noCache: true,
          order: 3
        }
      },
      {
        path: 'custom-functions',
        component: () => import('@/views/PushCustomFunctions/PushCustomFunctions.vue'),
        name: 'PushCustomFunctions',
        meta: {
          title: '推送函数',
          icon: 'vi-mdi:function-variant',
          noCache: true,
          order: 4
        }
      },
      {
        path: 'worker-status',
        component: () => import('@/views/PushWorkerStatus/PushWorkerStatus.vue'),
        name: 'PushWorkerStatus',
        meta: {
          title: 'Worker 运行状态',
          icon: 'vi-mdi:heart-pulse',
          noCache: true,
          order: 5
        }
      },
      {
        path: 'futu-opend',
        component: () => import('@/views/FutuOpenD/FutuOpenD.vue'),
        name: 'FutuOpenD',
        meta: {
          title: 'Futu OpenD',
          icon: 'vi-mdi:server-network',
          noCache: true,
          order: 6
        }
      },
      {
        path: 'moomoo-opend',
        component: () => import('@/views/MoomooOpenD/MoomooOpenD.vue'),
        name: 'MoomooOpenD',
        meta: {
          title: 'Moomoo OpenD',
          icon: 'vi-mdi:server-network',
          noCache: true,
          order: 6
        }
      },
      {
        path: 'settings',
        component: () => import('@/views/PushSystemSettings/PushSystemSettings.vue'),
        name: 'PushSystemSettings',
        meta: {
          title: '系统设置',
          icon: 'vi-mdi:cog-outline',
          noCache: true,
          order: 7
        }
      },
      {
        path: 'market-holidays',
        component: () => import('@/views/MarketHolidays/MarketHolidays.vue'),
        name: 'MarketHolidays',
        meta: {
          title: '市场假日',
          icon: 'vi-mdi:calendar-remove-outline',
          noCache: true,
          order: 7.5
        }
      },
      {
        path: 'media-channels',
        component: () => import('@/views/MediaChannels/MediaChannels.vue'),
        name: 'MediaChannelsAdmin',
        meta: {
          title: '媒体频道',
          icon: 'vi-mdi:play-network-outline',
          noCache: true,
          order: 8
        }
      },
      {
        path: 'llm-eval/dataset',
        component: () => import('@/views/LLMEval/EvalDataset.vue'),
        name: 'LLMEvalDataset',
        meta: {
          title: 'LLM Eval — Dataset',
          icon: 'vi-mdi:checkbox-marked-circle-auto-outline',
          noCache: true,
          order: 9
        }
      }
    ]
  },
  // ========================================
  // 运营工具（业务运营 / 增长 / 内容 / 客服）
  // 判定标准: docs/CONTRIBUTING.md 路由分组规则
  // ========================================
  {
    path: '/operations',
    component: Layout,
    redirect: '/operations/growth-dashboard',
    name: 'OperationsSection',
    meta: {
      title: '运营工具',
      icon: 'vi-mdi:chart-line-variant',
      alwaysShow: true,
      order: 0.5
    },
    children: [
      {
        path: 'growth-dashboard',
        component: () => import('@/views/Operations/GrowthDashboard/GrowthDashboardHome.vue'),
        name: 'OperationsGrowthDashboard',
        meta: {
          title: '用户增长',
          icon: 'vi-mdi:chart-line',
          noCache: true,
          order: 1
        }
      },
      {
        path: 'growth-dashboard/cohort',
        component: () => import('@/views/Operations/GrowthDashboard/CohortQuery.vue'),
        name: 'OperationsCohortQuery',
        meta: {
          title: 'Cohort 查询',
          hidden: true,
          canTo: true,
          activeMenu: '/operations/growth-dashboard',
          noCache: true
        }
      },
      {
        path: 'growth-dashboard/monthly-statements',
        component: () => import('@/views/Operations/GrowthDashboard/MonthlyStatementList.vue'),
        name: 'OperationsMonthlyStatement',
        meta: {
          title: '月度对账单',
          hidden: true,
          canTo: true,
          activeMenu: '/operations/growth-dashboard',
          noCache: true
        }
      },
      {
        path: 'wechat-groups',
        component: getParentLayout(),
        redirect: '/operations/wechat-groups/rules',
        name: 'OperationsWechatGroups',
        meta: {
          title: '微信群管理',
          icon: 'vi-mdi:account-group-outline',
          alwaysShow: true,
          order: 2
        },
        children: [
          {
            path: 'rules',
            component: () => import('@/views/Operations/WechatGroups/WechatGroupRules.vue'),
            name: 'OperationsWechatGroupRules',
            meta: {
              title: '微信群规',
              icon: 'vi-mdi:clipboard-text-outline',
              noCache: true,
              order: 1
            }
          },
          {
            path: 'members',
            component: () => import('@/views/Operations/WechatGroups/WechatGroupMembers.vue'),
            name: 'OperationsWechatGroupMembers',
            meta: {
              title: '群成员',
              icon: 'vi-mdi:account-supervisor-outline',
              noCache: true,
              order: 2
            }
          },
          {
            path: 'blacklist',
            component: () => import('@/views/Operations/WechatGroups/WechatBlacklist.vue'),
            name: 'OperationsWechatBlacklist',
            meta: {
              title: '群黑名单',
              icon: 'vi-mdi:account-cancel-outline',
              noCache: true,
              order: 3
            }
          }
        ]
      },
      // Backwards-compat redirects for old bookmarked URLs.
      {
        path: 'wechat-group-rules',
        redirect: '/operations/wechat-groups/rules',
        name: 'OperationsWechatGroupRulesLegacy',
        meta: { hidden: true }
      },
      {
        path: 'wechat-group-members',
        redirect: '/operations/wechat-groups/members',
        name: 'OperationsWechatGroupMembersLegacy',
        meta: { hidden: true }
      },
      // Moved to /ai-employees/operator (#836). Redirect-only stub keeps old
      // bookmarks working. canTo:true is required so adminRouteFilter doesn't
      // strip the redirect entry from the registered router.
      {
        path: 'wechat-publish',
        redirect: '/ai-employees/operator',
        name: 'OperationsWechatPublishRedirect',
        meta: { hidden: true, canTo: true, noCache: true }
      },
      {
        path: 'agent-credentials',
        component: () => import('@/views/Operations/AgentCredentials/AgentCredentials.vue'),
        name: 'OperationsAgentCredentials',
        meta: {
          title: 'AI Agent 凭证',
          icon: 'vi-mdi:key-variant',
          noCache: true,
          order: 5
        }
      },
      {
        path: 'wechat-agent',
        component: () => import('@/views/Operations/WechatAgent/WechatAgent.vue'),
        name: 'OperationsWechatAgent',
        meta: {
          title: '微信 AI 员工',
          icon: 'vi-mdi:robot-outline',
          noCache: true,
          order: 4
        }
      },
      {
        path: 'customer-service',
        component: () => import('@/views/OpsTools/CustomerService/CustomerServiceInbox.vue'),
        name: 'CustomerServiceInboxView',
        meta: {
          title: '客服中心',
          icon: 'vi-mdi:account-tie-voice-outline',
          noCache: true,
          order: 4
        }
      },
      {
        path: 'customer-service-staff',
        component: () => import('@/views/OpsTools/CustomerService/CustomerServiceStaff.vue'),
        name: 'CustomerServiceStaffView',
        meta: {
          title: '客服员工管理',
          icon: 'vi-mdi:account-cog-outline',
          noCache: true,
          order: 5
        }
      },
      {
        path: 'system-settings',
        component: () => import('@/views/Operations/SystemSettings/SystemSettings.vue'),
        name: 'OperationsSystemSettings',
        meta: {
          title: '系统设置',
          icon: 'vi-mdi:cog-outline',
          noCache: true,
          order: 9
        }
      },
      {
        path: 'api-tokens',
        component: () => import('@/views/Personal/ApiTokens/ApiTokens.vue'),
        name: 'OperationsApiTokens',
        meta: {
          title: 'API Token 管理',
          icon: 'vi-mdi:key-chain-variant',
          noCache: true,
          order: 10
        }
      },
      // 量化策略 / 数据方法目录 / 策略审核 moved to the new top-level
      // `AI 策略管理` section (#488 / #519). Redirect-only stubs below keep
      // existing bookmarks working.
      {
        path: 'strategies',
        redirect: '/ai-strategy/strategies',
        name: 'OperationsStrategiesRedirect',
        meta: { hidden: true, noCache: true }
      },
      {
        path: 'strategies/:strategy_id',
        redirect: (to) => `/ai-strategy/strategies/${to.params.strategy_id}`,
        name: 'OperationsStrategyDetailRedirect',
        meta: { hidden: true, noCache: true }
      },
      {
        path: 'strategy-data-methods',
        redirect: '/ai-strategy/data-methods',
        name: 'OperationsStrategyDataMethodsRedirect',
        meta: { hidden: true, noCache: true }
      },
      {
        path: 'strategy-review',
        redirect: '/ai-strategy/strategy-review',
        name: 'OperationsStrategyReviewRedirect',
        meta: { hidden: true, noCache: true }
      },
      {
        path: 'strategy-review/:review_id',
        redirect: (to) => `/ai-strategy/strategy-review/${to.params.review_id}`,
        name: 'OperationsStrategyReviewDetailRedirect',
        meta: { hidden: true, noCache: true }
      }
    ]
  },
  // ========================================
  // AI 员工 (#836) — 集中 Discord/微信/客服等 AI 员工类页面入口。
  // 本期(#836)只搬迁 AI 运营发布 + 建两个占位页;后续功能各自单独 issue。
  // ========================================
  {
    path: '/ai-employees',
    component: Layout,
    redirect: '/ai-employees/operator',
    name: 'AIEmployeesSection',
    meta: {
      title: 'AI 员工',
      icon: 'vi-mdi:account-tie-hat-outline',
      alwaysShow: true,
      order: 0.53
    },
    children: [
      {
        path: 'wechat-kefu',
        component: () => import('@/views/AIEmployees/AutoReplyRules.vue'),
        name: 'AIEmployeesWechatKefu',
        meta: {
          title: 'AI 微信客服',
          icon: 'vi-mdi:face-agent',
          noCache: true,
          order: 1
        }
      },
      {
        // Same component as the old /operations/wechat-publish (#836).
        // Component path kept under views/Operations/WechatPublish/ so git
        // blame stays useful; only the route mount point + name moved.
        path: 'operator',
        component: () => import('@/views/Operations/WechatPublish/WechatPublish.vue'),
        name: 'AIEmployeesOperator',
        meta: {
          title: 'AI 运营',
          icon: 'vi-mdi:broadcast',
          noCache: true,
          order: 2
        }
      },
      {
        path: 'overview',
        component: () => import('@/views/AIEmployees/Placeholder.vue'),
        name: 'AIEmployeesOverview',
        meta: {
          title: 'AI 员工',
          icon: 'vi-mdi:account-group-outline',
          noCache: true,
          order: 3
        }
      }
    ]
  },
  // ========================================
  // AI 策略管理（量化策略 / 数据方法目录 / 我的策略）
  // Promoted out of 运营工具 (#488) — strategy work has grown beyond
  // an operations sub-section and now warrants its own area.
  // ========================================
  {
    path: '/ai-strategy',
    component: Layout,
    redirect: '/ai-strategy/strategies',
    name: 'AIStrategySection',
    meta: {
      title: 'AI 策略管理',
      icon: 'vi-mdi:robot-outline',
      alwaysShow: true,
      order: 0.55
    },
    children: [
      {
        path: 'strategies',
        component: () => import('@/views/Operations/Strategies/Strategies.vue'),
        name: 'AIStrategyStrategies',
        meta: {
          title: '量化策略管理',
          icon: 'vi-mdi:chart-line',
          noCache: true,
          order: 1
        }
      },
      {
        path: 'strategies/:strategy_id',
        component: () => import('@/views/Operations/Strategies/StrategyDetail.vue'),
        name: 'AIStrategyStrategyDetail',
        meta: {
          title: '策略详情',
          hidden: true,
          canTo: true,
          activeMenu: '/ai-strategy/strategies',
          noCache: true
        }
      },
      {
        path: 'data-methods',
        component: () => import('@/views/Operations/Strategies/StrategyDataMethodsCatalog.vue'),
        name: 'AIStrategyDataMethods',
        meta: {
          title: '策略数据方法目录',
          icon: 'vi-mdi:book-open-variant',
          noCache: true,
          order: 2
        }
      },
      // #488 PR2 — admin端"我的策略" page, ported from customer-frontend.
      // Calls the same /api/customer/strategy-ai/* endpoints (admin user
      // is a customer too; JWT user_id resolves to the operator's own
      // strategies, no backend changes needed).
      {
        path: 'my-strategies',
        component: () => import('@/views/AIStrategy/MyStrategies/MyStrategiesList.vue'),
        name: 'AIStrategyMyStrategies',
        meta: {
          title: '我的策略',
          icon: 'vi-mdi:account-cog-outline',
          noCache: true,
          order: 3
        }
      },
      {
        path: 'my-strategies/:strategyId',
        component: () => import('@/views/AIStrategy/MyStrategies/MyStrategyDetail.vue'),
        name: 'AIStrategyMyStrategyDetail',
        meta: {
          title: '策略详情',
          hidden: true,
          canTo: true,
          activeMenu: '/ai-strategy/my-strategies',
          noCache: true
        }
      },
      // #519 — strategy-review queue + detail moved out of 运营工具.
      // Component path unchanged (src/views/Operations/StrategyReview/*)
      // so legacy git blame stays useful; only the route mount point + name moved.
      {
        path: 'strategy-review',
        component: () => import('@/views/Operations/StrategyReview/StrategyReviewQueue.vue'),
        name: 'AIStrategyReviewQueue',
        meta: {
          title: '策略审核',
          icon: 'vi-mdi:shield-check-outline',
          noCache: true,
          order: 4
        }
      },
      {
        path: 'strategy-review/:review_id',
        component: () => import('@/views/Operations/StrategyReview/StrategyReviewDetail.vue'),
        name: 'AIStrategyReviewDetail',
        meta: {
          title: '审核详情',
          hidden: true,
          canTo: true,
          activeMenu: '/ai-strategy/strategy-review',
          noCache: true
        }
      },
      // 小秘书平台配额用量 dashboard (#2193 / EPIC #2184 P0-c). Per-user
      // monthly platform-conversation usage, sits just before 系统设置 where
      // the quota value itself is configured.
      {
        path: 'usage',
        component: () => import('@/views/AIStrategy/Usage/StrategyAiUsage.vue'),
        name: 'AIStrategyUsage',
        meta: {
          title: '小秘书用量',
          icon: 'vi-mdi:chart-donut',
          noCache: true,
          order: 4.5
        }
      },
      // Focused page showing ONLY the ``anthropic_platform_user_strategy_ai``
      // slot (the platform-paid Anthropic key used by AI 量化策略 chat as
      // a fallback when a user has not configured their own BYOK key).
      // Full API-key admin still lives at the hidden /ai-traders/api-keys
      // page for breadth.
      {
        path: 'system-settings',
        component: () => import('@/views/AIStrategy/SystemSettings/StrategyAIKeySettings.vue'),
        name: 'AIStrategySystemSettings',
        meta: {
          title: '系统设置',
          icon: 'vi-mdi:cog',
          noCache: true,
          order: 5
        }
      }
    ]
  },
  // ========================================
  // 运维工具（SRE / 工程师 — 系统监控、性能、配置）
  // 判定标准: docs/CONTRIBUTING.md 路由分组规则
  // ========================================
  {
    path: '/sre',
    component: Layout,
    redirect: '/sre/system-health',
    name: 'SreSection',
    meta: {
      title: '运维工具',
      icon: 'vi-mdi:wrench-cog-outline',
      alwaysShow: true,
      order: 0.9
    },
    children: [
      {
        path: 'system-health',
        component: () => import('@/views/Sre/SystemHealth.vue'),
        name: 'SreSystemHealth',
        meta: {
          title: '系统健康',
          icon: 'vi-mdi:heart-pulse',
          noCache: true,
          order: 0
        }
      },
      {
        path: 'api-performance',
        component: () => import('@/views/Sre/ApiPerformance/ApiPerformanceList.vue'),
        name: 'SreApiPerformanceList',
        meta: {
          title: 'API 性能',
          icon: 'vi-mdi:speedometer',
          noCache: true,
          order: 1
        }
      },
      {
        path: 'api-performance/:endpoint',
        component: () => import('@/views/Sre/ApiPerformance/ApiPerformanceDetail.vue'),
        name: 'SreApiPerformanceDetail',
        meta: {
          title: 'API 性能详情',
          hidden: true,
          activeMenu: '/sre/api-performance',
          noCache: true
        }
      },
      {
        path: 'push-flow-inspector',
        component: () => import('@/views/PushFlowInspector/PushFlowInspector.vue'),
        name: 'SrePushFlowInspector',
        meta: {
          title: '推送链路检查',
          icon: 'vi-mdi:sitemap-outline',
          noCache: true,
          order: 2
        }
      },
      {
        path: 'server-error-logs',
        component: () => import('@/views/ServerErrors/ServerErrorLogs.vue'),
        name: 'SreServerErrorLogs',
        meta: {
          title: '服务端异常日志',
          icon: 'vi-mdi:alert-octagon-outline',
          noCache: true,
          order: 3
        }
      },
      {
        path: 'wechat-bot-logs',
        component: () => import('@/views/Sre/WeChatBotLogs/Index.vue'),
        name: 'SreWeChatBotLogs',
        meta: {
          title: '微信原始流水',
          icon: 'vi-mdi:wechat',
          noCache: true,
          order: 3.5
        }
      },
      {
        path: 'admin-deployments',
        component: () => import('@/views/Sre/AdminDeployments.vue'),
        name: 'SreAdminDeployments',
        meta: {
          title: 'Admin Deployments',
          icon: 'vi-mdi:rocket-launch-outline',
          noCache: true,
          order: 3.7
        }
      },
      {
        path: 'system-settings',
        component: () => import('@/views/Sre/SystemSettings.vue'),
        name: 'SreSystemSettings',
        meta: {
          title: '系统设置',
          icon: 'vi-mdi:cog-outline',
          noCache: true,
          order: 4
        }
      },
      {
        path: 'admin-audit-logs',
        component: () => import('@/views/AdminAudit/AuditLog.vue'),
        name: 'SreAdminAuditLogs',
        meta: {
          title: '操作审计',
          icon: 'vi-mdi:clipboard-text-search-outline',
          noCache: true,
          order: 5
        }
      }
    ]
  },
  // ========================================
  // Auto Trading（独立顶级菜单，与推送系统并列）
  // ========================================
  {
    path: '/auto-trading',
    component: Layout,
    redirect: '/auto-trading/signal-parse',
    name: 'AutoTradingSection',
    meta: {
      title: '自动交易',
      icon: 'vi-mdi:robot-outline',
      alwaysShow: true,
      order: 0.52
    },
    children: [
      {
        path: 'docker-agents',
        component: () => import('@/views/PushAutoTrading/AutoTradeAgents.vue'),
        name: 'AutoTradeDockerAgents',
        meta: {
          title: '自动交易客户端',
          icon: 'vi-mdi:docker',
          noCache: true,
          order: 1.8
        }
      },
      {
        path: 'virtual-followers',
        component: () => import('@/views/PushAutoTrading/VirtualFollowers.vue'),
        name: 'VirtualFollowers',
        meta: {
          title: '虚拟跟单员',
          icon: 'vi-mdi:account-multiple-outline',
          noCache: true,
          order: 1.85
        }
      },
      {
        path: 'signal-history',
        component: () => import('@/views/PushAutoTrading/TradeSignalHistory.vue'),
        name: 'AutoTradingSignalHistory',
        meta: {
          title: '历史喊单',
          icon: 'vi-mdi:history',
          noCache: true,
          order: 2
        }
      },
      {
        path: 'signal-parse',
        component: () => import('@/views/PushAutoTrading/SignalParse.vue'),
        name: 'AutoTradingSignalParse',
        meta: {
          title: '喊单解析',
          icon: 'vi-mdi:text-search',
          noCache: true,
          order: 1
        }
      },
      {
        path: 'roll-up-tasks',
        component: () => import('@/views/PushAutoTrading/RollUpTasks.vue'),
        name: 'RollUpTasks',
        meta: {
          title: 'ROLL_UP 任务',
          icon: 'vi-mdi:swap-horizontal',
          noCache: true,
          order: 1.27
        }
      },
      {
        path: 'wechat-callers',
        component: () => import('@/views/WechatCallers/WechatCallers.vue'),
        name: 'WechatCallers',
        meta: {
          title: '微信喊单员',
          icon: 'vi-mdi:wechat',
          noCache: true,
          order: 1.4
        }
      },
      {
        path: 'wechat-caller-settings',
        component: () => import('@/views/WechatCallers/WechatCallerSettings.vue'),
        name: 'WechatCallerSettings',
        meta: {
          title: '微信喊单员设置',
          icon: 'vi-mdi:wechat',
          noCache: true,
          order: 1.41
        }
      },
      {
        path: 'system-settings',
        component: () => import('@/views/PushAutoTrading/AutoTradeSystemSettings.vue'),
        name: 'AutoTradeSystemSettings',
        meta: {
          title: '系统设置',
          icon: 'vi-mdi:cog-outline',
          noCache: true,
          order: 99
        }
      }
    ]
  },
  // ========================================
  // AI Traders Section (US2, US3, US4)
  // ========================================
  {
    path: '/ai-traders',
    component: Layout,
    redirect: '/ai-traders/broker-config',
    name: 'AITradersSection',
    meta: {
      title: 'AI Traders',
      icon: 'vi-mdi:robot',
      alwaysShow: true,
      order: 1
    },
    children: [
      // Surfaced from the sidebar under AI 策略管理 → 系统设置 (operator
      // ask, 2026-06-09). Route kept reachable for existing bookmarks; the
      // duplicate menu entry would just confuse where the key actually
      // lives.
      {
        path: 'api-keys',
        component: () => import('@/views/ApiKeys/ApiKeysManagement.vue'),
        name: 'ApiKeysManagement',
        meta: {
          title: 'API Key Configuration',
          icon: 'vi-mdi:key',
          noCache: true,
          hidden: true,
          canTo: true,
          order: 1
        }
      },
      {
        path: 'broker-config',
        component: () => import('@/views/Broker/BrokerManagement.vue'),
        name: 'BrokerManagement',
        meta: {
          title: 'Broker Configuration',
          icon: 'vi-carbon:finance',
          noCache: true,
          order: 2
        }
      },
      {
        path: 'positions',
        component: () => import('@/views/Positions/Positions.vue'),
        name: 'PositionsView',
        meta: {
          title: 'Account Positions',
          icon: 'vi-mdi:briefcase-variant',
          noCache: true,
          order: 3
        }
      },
      {
        path: 'virtual-accounts',
        component: () => import('@/views/VirtualAccounts/VirtualAccounts.vue'),
        name: 'VirtualAccountsView',
        meta: {
          title: 'Virtual Accounts',
          icon: 'vi-mdi:wallet',
          noCache: true,
          order: 4
        }
      },
      {
        path: 'watchlists',
        component: () => import('@/views/Watchlists/Watchlists.vue'),
        name: 'WatchlistsView',
        meta: {
          title: 'Stock Watchlists',
          icon: 'vi-mdi:bookmark-multiple',
          noCache: true,
          order: 5
        }
      },
      {
        path: 'screener',
        component: () => import('@/views/Screener/ScreenerPage.vue'),
        name: 'ScreenerView',
        meta: {
          title: 'AI Stock Screener',
          icon: 'vi-mdi:magnify-scan',
          noCache: true,
          order: 6
        }
      },
      {
        path: 'stock',
        redirect: { name: 'StockDetail', params: { symbol: 'AAPL' }, query: { market: 'US' } },
        name: 'StockDetailEntry',
        meta: {
          title: 'Stock Detail',
          icon: 'vi-mdi:chart-box-outline',
          noCache: true,
          order: 7
        }
      },
      {
        path: 'stock/:symbol',
        component: () => import('@/views/Screener/StockDetail.vue'),
        name: 'StockDetail',
        meta: {
          title: 'Stock Details',
          hidden: true,
          noCache: true,
          canTo: true,
          activeMenu: '/ai-traders/stock'
        }
      },
      {
        path: 'agent-tools',
        component: () => import('@/views/Tools/ToolsManagement.vue'),
        name: 'ToolsManagement',
        meta: {
          title: 'Agent Tools',
          icon: 'vi-mdi:tools',
          noCache: true,
          order: 6
        }
      },
      {
        path: 'strategies',
        component: () => import('@/views/Strategies/Strategies.vue'),
        name: 'StrategiesView',
        meta: {
          title: 'Trading Strategies',
          icon: 'vi-mdi:lightbulb-on',
          noCache: true,
          order: 7
        }
      },
      {
        path: 'strategy-code',
        component: () => import('@/pages/StrategyCodePage.vue'),
        name: 'StrategyCodeView',
        meta: {
          title: 'AI Code Generator',
          icon: 'vi-mdi:code-braces',
          noCache: true,
          order: 7.5
        }
      },
      {
        path: 'data-functions',
        component: () => import('@/views/DataFunctions/DataFunctionManagement.vue'),
        name: 'DataFunctionManagement',
        meta: {
          title: t('router.dataFunctionManagement'),
          icon: 'vi-mdi:function',
          noCache: true,
          order: 7.6
        }
      },
      {
        path: 'code-import-rules',
        component: () => import('@/views/DataFunctions/CodeImportRules.vue'),
        name: 'CodeImportRules',
        meta: {
          title: t('router.codeImportRules'),
          icon: 'vi-mdi:shield-lock-outline',
          noCache: true,
          order: 7.65
        }
      },
      {
        path: 'trading-agents',
        component: () => import('@/views/TradingAgents/AITraders.vue'),
        name: 'TradingAgentsView',
        meta: {
          title: 'Trading Agents',
          icon: 'vi-mdi:robot-outline',
          noCache: true,
          order: 8
        }
      },
      {
        path: 'trader-arena',
        component: () => import('@/views/TraderArena/TraderArena.vue'),
        name: 'TraderArenaView',
        meta: {
          title: 'Trader Arena',
          icon: 'vi-mdi:trophy-outline',
          noCache: true,
          order: 9
        }
      },
      {
        path: 'wechat-bot-messages',
        component: () => import('@/views/WechatBot/WechatBotMessages.vue'),
        name: 'WechatBotMessagesView',
        meta: {
          title: '微信机器人消息',
          icon: 'vi-mdi:chat-processing-outline',
          noCache: true,
          order: 10
        }
      }
    ]
  },
  // ========================================
  // 云券商 (Cloud Broker) — Pod 管理 + 系统设置 (shared creds).
  // Pod page was previously under /operations/cloud-broker-pods (#576);
  // moved here in #798 alongside the new SystemSettings page.
  // ========================================
  {
    path: '/cloud-broker',
    component: Layout,
    redirect: '/cloud-broker/pods',
    name: 'CloudBrokerSection',
    meta: {
      title: '云券商',
      icon: 'vi-mdi:cloud-cog-outline',
      alwaysShow: true,
      order: 1.05
    },
    children: [
      {
        path: 'pods',
        component: () => import('@/views/CloudBroker/Pods/CloudBrokerPods.vue'),
        name: 'CloudBrokerPods',
        meta: {
          title: 'Pod 管理',
          icon: 'vi-mdi:server-network',
          noCache: true,
          order: 1
        }
      },
      {
        // Phase 2.5b (#548). Hidden route — reached by direct URL with a
        // cloud-broker VirtualAccount id. Surfaces noVNC + port-forward
        // info for ops-side diagnosis of stuck IB Gateway pods.
        path: ':accountId/vnc',
        component: () => import('@/views/CloudBroker/Pods/CloudBrokerVNC.vue'),
        name: 'CloudBrokerVNC',
        meta: {
          title: 'IB Gateway VNC',
          hidden: true,
          canTo: true,
          activeMenu: '/cloud-broker/pods',
          noCache: true
        }
      },
      {
        // #1384 — image-tag drift sweep for cloud-broker runner Deployments.
        path: 'runner-images',
        component: () => import('@/views/CloudBroker/RunnerImages/RunnerImageStatus.vue'),
        name: 'CloudBrokerRunnerImages',
        meta: {
          title: 'Runner 镜像状态',
          icon: 'vi-mdi:image-sync-outline',
          noCache: true,
          order: 2
        }
      },
      {
        // #1768 — moved out of 「自动交易 → 系统设置」 into its own cloud-broker
        // subpage; controls the mobile picker's 云券商显示 opt-out switches.
        path: 'system-settings',
        component: () => import('@/views/CloudBroker/SystemSettings/CloudBrokerSystemSettings.vue'),
        name: 'CloudBrokerSystemSettings',
        meta: {
          title: '系统设置',
          icon: 'vi-mdi:cog-outline',
          noCache: true,
          order: 3
        }
      }
    ]
  },
  {
    path: '/guide',
    component: Layout,
    name: 'Guide',
    meta: {
      hidden: true // Hidden: Template page (US1)
    },
    children: [
      {
        path: 'index',
        component: () => import('@/views/Guide/Guide.vue'),
        name: 'GuideDemo',
        meta: {
          title: t('router.guide'),
          icon: 'vi-cib:telegram-plane'
        }
      }
    ]
  },
  {
    path: '/components',
    component: Layout,
    name: 'ComponentsDemo',
    meta: {
      title: t('router.component'),
      icon: 'vi-bx:bxs-component',
      alwaysShow: true,
      hidden: true // Hidden: Template page (US1)
    },
    children: [
      {
        path: 'form',
        component: getParentLayout(),
        redirect: '/components/form/default-form',
        name: 'Form',
        meta: {
          title: t('router.form'),
          alwaysShow: true
        },
        children: [
          {
            path: 'default-form',
            component: () => import('@/views/Components/Form/DefaultForm.vue'),
            name: 'DefaultForm',
            meta: {
              title: t('router.defaultForm')
            }
          },
          {
            path: 'use-form',
            component: () => import('@/views/Components/Form/UseFormDemo.vue'),
            name: 'UseForm',
            meta: {
              title: 'UseForm'
            }
          }
        ]
      },
      {
        path: 'table',
        component: getParentLayout(),
        redirect: '/components/table/default-table',
        name: 'TableDemo',
        meta: {
          title: t('router.table'),
          alwaysShow: true
        },
        children: [
          {
            path: 'default-table',
            component: () => import('@/views/Components/Table/DefaultTable.vue'),
            name: 'DefaultTable',
            meta: {
              title: t('router.defaultTable')
            }
          },
          {
            path: 'use-table',
            component: () => import('@/views/Components/Table/UseTableDemo.vue'),
            name: 'UseTable',
            meta: {
              title: 'UseTable'
            }
          },
          {
            path: 'tree-table',
            component: () => import('@/views/Components/Table/TreeTable.vue'),
            name: 'TreeTable',
            meta: {
              title: t('router.treeTable')
            }
          },
          {
            path: 'table-image-preview',
            component: () => import('@/views/Components/Table/TableImagePreview.vue'),
            name: 'TableImagePreview',
            meta: {
              title: t('router.PicturePreview')
            }
          },
          {
            path: 'table-video-preview',
            component: () => import('@/views/Components/Table/TableVideoPreview.vue'),
            name: 'TableVideoPreview',
            meta: {
              title: t('router.tableVideoPreview')
            }
          },
          {
            path: 'card-table',
            component: () => import('@/views/Components/Table/CardTable.vue'),
            name: 'CardTable',
            meta: {
              title: t('router.cardTable')
            }
          }
        ]
      },
      {
        path: 'editor-demo',
        component: getParentLayout(),
        redirect: '/components/editor-demo/editor',
        name: 'EditorDemo',
        meta: {
          title: t('router.editor'),
          alwaysShow: true
        },
        children: [
          {
            path: 'editor',
            component: () => import('@/views/Components/Editor/Editor.vue'),
            name: 'Editor',
            meta: {
              title: t('router.richText')
            }
          },
          {
            path: 'json-editor',
            component: () => import('@/views/Components/Editor/JsonEditor.vue'),
            name: 'JsonEditor',
            meta: {
              title: t('router.jsonEditor')
            }
          }
        ]
      },
      {
        path: 'search',
        component: () => import('@/views/Components/Search.vue'),
        name: 'Search',
        meta: {
          title: t('router.search')
        }
      },
      {
        path: 'descriptions',
        component: () => import('@/views/Components/Descriptions.vue'),
        name: 'Descriptions',
        meta: {
          title: t('router.descriptions')
        }
      },
      {
        path: 'image-viewer',
        component: () => import('@/views/Components/ImageViewer.vue'),
        name: 'ImageViewer',
        meta: {
          title: t('router.imageViewer')
        }
      },
      {
        path: 'dialog',
        component: () => import('@/views/Components/Dialog.vue'),
        name: 'Dialog',
        meta: {
          title: t('router.dialog')
        }
      },
      {
        path: 'icon',
        component: () => import('@/views/Components/Icon.vue'),
        name: 'Icon',
        meta: {
          title: t('router.icon')
        }
      },
      {
        path: 'icon-picker',
        component: () => import('@/views/Components/IconPicker.vue'),
        name: 'IconPicker',
        meta: {
          title: t('router.iconPicker')
        }
      },
      {
        path: 'echart',
        component: () => import('@/views/Components/Echart.vue'),
        name: 'Echart',
        meta: {
          title: t('router.echart')
        }
      },
      {
        path: 'count-to',
        component: () => import('@/views/Components/CountTo.vue'),
        name: 'CountTo',
        meta: {
          title: t('router.countTo')
        }
      },
      {
        path: 'qrcode',
        component: () => import('@/views/Components/Qrcode.vue'),
        name: 'Qrcode',
        meta: {
          title: t('router.qrcode')
        }
      },
      {
        path: 'highlight',
        component: () => import('@/views/Components/Highlight.vue'),
        name: 'Highlight',
        meta: {
          title: t('router.highlight')
        }
      },
      {
        path: 'infotip',
        component: () => import('@/views/Components/Infotip.vue'),
        name: 'Infotip',
        meta: {
          title: t('router.infotip')
        }
      },
      {
        path: 'input-password',
        component: () => import('@/views/Components/InputPassword.vue'),
        name: 'InputPassword',
        meta: {
          title: t('router.inputPassword')
        }
      },
      {
        path: 'waterfall',
        component: () => import('@/views/Components/Waterfall.vue'),
        name: 'waterfall',
        meta: {
          title: t('router.waterfall')
        }
      },
      {
        path: 'image-cropping',
        component: () => import('@/views/Components/ImageCropping.vue'),
        name: 'ImageCropping',
        meta: {
          title: t('router.imageCropping')
        }
      },
      {
        path: 'video-player',
        component: () => import('@/views/Components/VideoPlayer.vue'),
        name: 'VideoPlayer',
        meta: {
          title: t('router.videoPlayer')
        }
      },
      {
        path: 'avatars',
        component: () => import('@/views/Components/Avatars.vue'),
        name: 'Avatars',
        meta: {
          title: t('router.avatars')
        }
      },
      {
        path: 'i-agree',
        component: () => import('@/views/Components/IAgree.vue'),
        name: 'IAgree',
        meta: {
          title: t('router.iAgree')
        }
      }
    ]
  },
  {
    path: '/function',
    component: Layout,
    redirect: '/function/multipleTabs',
    name: 'Function',
    meta: {
      title: t('router.function'),
      icon: 'vi-ri:function-fill',
      alwaysShow: true,
      hidden: true // Hidden: Template page (US1)
    },
    children: [
      {
        path: 'multiple-tabs',
        component: () => import('@/views/Function/MultipleTabs.vue'),
        name: 'MultipleTabs',
        meta: {
          title: t('router.multipleTabs')
        }
      },
      {
        path: 'multiple-tabs-demo/:id',
        component: () => import('@/views/Function/MultipleTabsDemo.vue'),
        name: 'MultipleTabsDemo',
        meta: {
          hidden: true,
          title: t('router.details'),
          canTo: true,
          activeMenu: '/function/multiple-tabs'
        }
      },
      {
        path: 'request',
        component: () => import('@/views/Function/Request.vue'),
        name: 'Request',
        meta: {
          title: t('router.request')
        }
      },
      {
        path: 'test',
        component: () => import('@/views/Function/Test.vue'),
        name: 'Test',
        meta: {
          title: t('router.permission'),
          permission: ['add', 'edit', 'delete']
        }
      }
    ]
  },
  {
    path: '/hooks',
    component: Layout,
    redirect: '/hooks/useWatermark',
    name: 'Hooks',
    meta: {
      title: 'hooks',
      icon: 'vi-ic:outline-webhook',
      alwaysShow: true,
      hidden: true // Hidden: Template page (US1)
    },
    children: [
      {
        path: 'useWatermark',
        component: () => import('@/views/hooks/useWatermark.vue'),
        name: 'UseWatermark',
        meta: {
          title: 'useWatermark'
        }
      },
      {
        path: 'useTagsView',
        component: () => import('@/views/hooks/useTagsView.vue'),
        name: 'UseTagsView',
        meta: {
          title: 'useTagsView'
        }
      },
      {
        path: 'useValidator',
        component: () => import('@/views/hooks/useValidator.vue'),
        name: 'UseValidator',
        meta: {
          title: 'useValidator'
        }
      },
      {
        path: 'useCrudSchemas',
        component: () => import('@/views/hooks/useCrudSchemas.vue'),
        name: 'UseCrudSchemas',
        meta: {
          title: 'useCrudSchemas'
        }
      },
      {
        path: 'useClipboard',
        component: () => import('@/views/hooks/useClipboard.vue'),
        name: 'UseClipboard',
        meta: {
          title: 'useClipboard'
        }
      },
      {
        path: 'useNetwork',
        component: () => import('@/views/hooks/useNetwork.vue'),
        name: 'UseNetwork',
        meta: {
          title: 'useNetwork'
        }
      }
    ]
  },
  {
    path: '/level',
    component: Layout,
    redirect: '/level/menu1/menu1-1/menu1-1-1',
    name: 'Level',
    meta: {
      title: t('router.level'),
      icon: 'vi-carbon:skill-level-advanced',
      hidden: true // Hidden: Template page (US1)
    },
    children: [
      {
        path: 'menu1',
        name: 'Menu1',
        component: getParentLayout(),
        redirect: '/level/menu1/menu1-1/menu1-1-1',
        meta: {
          title: t('router.menu1')
        },
        children: [
          {
            path: 'menu1-1',
            name: 'Menu11',
            component: getParentLayout(),
            redirect: '/level/menu1/menu1-1/menu1-1-1',
            meta: {
              title: t('router.menu11'),
              alwaysShow: true
            },
            children: [
              {
                path: 'menu1-1-1',
                name: 'Menu111',
                component: () => import('@/views/Level/Menu111.vue'),
                meta: {
                  title: t('router.menu111')
                }
              }
            ]
          },
          {
            path: 'menu1-2',
            name: 'Menu12',
            component: () => import('@/views/Level/Menu12.vue'),
            meta: {
              title: t('router.menu12')
            }
          }
        ]
      },
      {
        path: 'menu2',
        name: 'Menu2',
        component: () => import('@/views/Level/Menu2.vue'),
        meta: {
          title: t('router.menu2')
        }
      }
    ]
  },
  {
    path: '/example',
    component: Layout,
    redirect: '/example/example-dialog',
    name: 'Example',
    meta: {
      title: t('router.example'),
      icon: 'vi-ep:management',
      alwaysShow: true,
      hidden: true // Hidden: Template page (US1)
    },
    children: [
      {
        path: 'example-dialog',
        component: () => import('@/views/Example/Dialog/ExampleDialog.vue'),
        name: 'ExampleDialog',
        meta: {
          title: t('router.exampleDialog')
        }
      },
      {
        path: 'example-page',
        component: () => import('@/views/Example/Page/ExamplePage.vue'),
        name: 'ExamplePage',
        meta: {
          title: t('router.examplePage')
        }
      },
      {
        path: 'example-add',
        component: () => import('@/views/Example/Page/ExampleAdd.vue'),
        name: 'ExampleAdd',
        meta: {
          title: t('router.exampleAdd'),
          noTagsView: true,
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/example/example-page'
        }
      },
      {
        path: 'example-edit',
        component: () => import('@/views/Example/Page/ExampleEdit.vue'),
        name: 'ExampleEdit',
        meta: {
          title: t('router.exampleEdit'),
          noTagsView: true,
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/example/example-page'
        }
      },
      {
        path: 'example-detail',
        component: () => import('@/views/Example/Page/ExampleDetail.vue'),
        name: 'ExampleDetail',
        meta: {
          title: t('router.exampleDetail'),
          noTagsView: true,
          noCache: true,
          hidden: true,
          canTo: true,
          activeMenu: '/example/example-page'
        }
      }
    ]
  },
  {
    path: '/error',
    component: Layout,
    redirect: '/error/404',
    name: 'Error',
    meta: {
      title: t('router.errorPage'),
      icon: 'vi-ci:error',
      alwaysShow: true,
      hidden: true // Hidden: Template page (US1)
    },
    children: [
      {
        path: '404-demo',
        component: () => import('@/views/Error/404.vue'),
        name: '404Demo',
        meta: {
          title: '404'
        }
      },
      {
        path: '403-demo',
        component: () => import('@/views/Error/403.vue'),
        name: '403Demo',
        meta: {
          title: '403'
        }
      },
      {
        path: '500-demo',
        component: () => import('@/views/Error/500.vue'),
        name: '500Demo',
        meta: {
          title: '500'
        }
      }
    ]
  },
  {
    path: '/authorization',
    component: Layout,
    redirect: '/authorization/user',
    name: 'Authorization',
    meta: {
      title: t('router.authorization'),
      icon: 'vi-eos-icons:role-binding',
      alwaysShow: true,
      hidden: true // Hidden: Template page (US1)
    },
    children: [
      {
        path: 'department',
        component: () => import('@/views/Authorization/Department/Department.vue'),
        name: 'Department',
        meta: {
          title: t('router.department')
        }
      },
      {
        path: 'user',
        component: () => import('@/views/Authorization/User/User.vue'),
        name: 'User',
        meta: {
          title: t('router.user')
        }
      },
      {
        path: 'menu',
        component: () => import('@/views/Authorization/Menu/Menu.vue'),
        name: 'Menu',
        meta: {
          title: t('router.menuManagement')
        }
      },
      {
        path: 'role',
        component: () => import('@/views/Authorization/Role/Role.vue'),
        name: 'Role',
        meta: {
          title: t('router.role')
        }
      }
    ]
  }
]

const router = createRouter({
  history: createWebHashHistory(),
  strict: true,
  routes: constantRouterMap as RouteRecordRaw[],
  scrollBehavior: () => ({ left: 0, top: 0 })
})

export const resetRouter = (): void => {
  router.getRoutes().forEach((route) => {
    const { name } = route
    if (name && !NO_RESET_WHITE_LIST.includes(name as string)) {
      router.hasRoute(name) && router.removeRoute(name)
    }
  })
}

export const setupRouter = (app: App<Element>) => {
  app.use(router)
}

export default router
